/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { Toast } from 'src/luis/projects/events/components/Toast/Toast';
import { ToasterPortal } from 'src/luis/projects/events/components/Toaster/ToasterPortal/ToasterPortal';

type Props = {
    /**
     * Quiets all the toast pop-ups when true.
     */
    quietAllToasts?: boolean;
};

/**
 * Represents a toaster, which is a floating container that holds
 * multiple toasts to display.
 */
export const Toaster = observer((props: Props) => {
    const { dispatcher, rootStore } = useGlobalState();

    const { quietAllToasts = false } = props;

    const toastStores = rootStore.toastStoreHouse.items;

    /**
     * Hides a toast when it is dismissed.
     *
     * @param id The toast id to hide.
     */
    const hideFloatingToast = (id: string) => {
        dispatcher.dispatch('hideFloatingToast', { id });
    };

    return (
        !quietAllToasts && (
            <ToasterPortal>
                <TransitionGroup>
                    {toastStores
                        .filter(n => !n.dismissed && (n.status === 'failed' || n.quiet !== true))
                        .map(toastStore => (
                                <CSSTransition key={toastStore.id} classNames="toast" timeout={250}>
                                    <Toast floating onDismiss={hideFloatingToast} toast={toastStore} />
                                </CSSTransition>
                            ))}
                </TransitionGroup>
            </ToasterPortal>
        )
    );
});
